import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 401, 403, or 404 errors
        if (error?.response?.status === 401 || 
            error?.response?.status === 403 || 
            error?.response?.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
    },
    mutations: {
      retry: false,
    },
  },
});

// Query key factory for consistent cache keys
export const queryKeys = {
  // Auth
  currentUser: ['user', 'current'] as const,
  
  // Schools
  schools: {
    all: ['schools'] as const,
    lists: () => [...queryKeys.schools.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.schools.lists(), params] as const,
    details: () => [...queryKeys.schools.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.schools.details(), id] as const,
    areas: (schoolId: string | number) => [...queryKeys.schools.detail(schoolId), 'areas'] as const,
    classes: (schoolId: string | number) => [...queryKeys.schools.detail(schoolId), 'classes'] as const,
    students: (schoolId: string | number) => [...queryKeys.schools.detail(schoolId), 'students'] as const,
  },
  
  // Students
  students: {
    all: ['students'] as const,
    lists: () => [...queryKeys.students.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.students.lists(), params] as const,
    details: () => [...queryKeys.students.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.students.details(), id] as const,
    activities: (id: string | number) => [...queryKeys.students.detail(id), 'activities'] as const,
    dailyReport: (id: string | number, date?: string) => [...queryKeys.students.detail(id), 'dailyReport', date] as const,
    attendance: (id: string | number) => [...queryKeys.students.detail(id), 'attendance'] as const,
    faceRecognition: (id: string | number) => [...queryKeys.students.detail(id), 'faceRecognition'] as const,
  },
  
  // Cameras
  cameras: {
    all: ['cameras'] as const,
    lists: () => [...queryKeys.cameras.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.cameras.lists(), params] as const,
    details: () => [...queryKeys.cameras.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.cameras.details(), id] as const,
  },
  
  // Areas
  areas: {
    all: ['areas'] as const,
    lists: () => [...queryKeys.areas.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.areas.lists(), params] as const,
    details: () => [...queryKeys.areas.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.areas.details(), id] as const,
  },
  
  // Detections
  detections: {
    all: ['detections'] as const,
    lists: () => [...queryKeys.detections.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.detections.lists(), params] as const,
    details: () => [...queryKeys.detections.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.detections.details(), id] as const,
    stats: (params?: any) => [...queryKeys.detections.all, 'stats', params] as const,
  },
  
  // Parent portal
  parent: {
    children: ['parent', 'children'] as const,
    childDetails: (childId: string | number) => ['parent', 'child', childId, 'details'] as const,
    childActivities: (childId: string | number) => ['parent', 'child', childId, 'activities'] as const,
    childDailyReport: (childId: string | number, date?: string) => ['parent', 'child', childId, 'dailyReport', date] as const,
    childDailyReports: (childId: string | number) => ['parent', 'child', childId, 'dailyReports'] as const,
    childPhotos: (childId: string | number) => ['parent', 'child', childId, 'photos'] as const,
    childAttendance: (childId: string | number) => ['parent', 'child', childId, 'attendance'] as const,
    notifications: ['parent', 'notifications'] as const,
    messages: ['parent', 'messages'] as const,
  },
  
  // Classes
  classes: {
    all: ['classes'] as const,
    lists: () => [...queryKeys.classes.all, 'list'] as const,
    list: (schoolId: string, params?: any) => [...queryKeys.classes.lists(), schoolId, params] as const,
    details: () => [...queryKeys.classes.all, 'detail'] as const,
    detail: (schoolId: string, classId: string) => [...queryKeys.classes.details(), schoolId, classId] as const,
    students: (schoolId: string, classId: string, params?: any) => [...queryKeys.classes.detail(schoolId, classId), 'students', params] as const,
  },
  
  // Notifications
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.notifications.lists(), params] as const,
    details: () => [...queryKeys.notifications.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.notifications.details(), id] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
  },
  
  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    overview: () => [...queryKeys.dashboard.all, 'overview'] as const,
    stats: (params?: any) => [...queryKeys.dashboard.all, 'stats', params] as const,
    activityFeed: (params?: any) => [...queryKeys.dashboard.all, 'activityFeed', params] as const,
  },
  
  // Alerts
  alerts: {
    all: ['alerts'] as const,
    lists: () => [...queryKeys.alerts.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.alerts.lists(), params] as const,
    details: () => [...queryKeys.alerts.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.alerts.details(), id] as const,
    stats: (params?: any) => [...queryKeys.alerts.all, 'stats', params] as const,
  },
  
  // Reports
  reports: {
    all: ['reports'] as const,
    lists: () => [...queryKeys.reports.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.reports.lists(), params] as const,
    details: () => [...queryKeys.reports.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.reports.details(), id] as const,
    metrics: (params?: any) => [...queryKeys.reports.all, 'metrics', params] as const,
  },
  
  // Teacher
  teacher: {
    all: ['teacher'] as const,
    profile: () => [...queryKeys.teacher.all, 'profile'] as const,
    classes: () => [...queryKeys.teacher.all, 'classes'] as const,
    classStudents: (classId: string | number) => [...queryKeys.teacher.all, 'class', classId, 'students'] as const,
    activitiesToVerify: () => [...queryKeys.teacher.all, 'activitiesToVerify'] as const,
    dailyReports: (params?: any) => [...queryKeys.teacher.all, 'dailyReports', params] as const,
  },
  
  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (params?: any) => [...queryKeys.users.lists(), params] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },
  
  // System
  system: {
    metrics: (params?: any) => ['system', 'metrics', params] as const,
    backgroundJobs: (params?: any) => ['system', 'backgroundJobs', params] as const,
    notifications: (params?: any) => ['system', 'notifications', params] as const,
    health: ['system', 'health'] as const,
    trainingDatasets: (params?: any) => ['system', 'trainingDatasets', params] as const,
    trainingJobs: (params?: any) => ['system', 'trainingJobs', params] as const,
    modelVersions: (params?: any) => ['system', 'modelVersions', params] as const,
  },
} as const;